import { Typography } from "@circle-react-uikit/Typography";
import type { FlaggedContentableType } from "@circle-react/types";
import { Link } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  COMMENT_TYPE,
  CONNECT_CONNECTION_TYPE,
  POST_TYPE,
} from "@/react/types/FlaggedContent";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

interface NavLinkStyleProps {
  className: string;
  onClick?: () => void | Promise<void> | Promise<unknown>;
}

export interface ContentActionTextProps {
  chatMessageUrl?: string | null;
  flaggedContentableId?: number;
  navLinkProps: NavLinkStyleProps;
  postTitle?: string;
  postUrl: string;
  recipientName?: string;
  recipientUrl?: string;
  spaceName?: string;
  spaceUrl: string;
  type: FlaggedContentableType;
}

export const ContentActionText = ({
  chatMessageUrl,
  flaggedContentableId,
  navLinkProps,
  postTitle,
  postUrl,
  recipientName,
  recipientUrl,
  spaceName,
  spaceUrl,
  type,
}: ContentActionTextProps) => {
  switch (type) {
    case POST_TYPE:
      return (
        <>
          <Link to={postUrl} {...navLinkProps}>
            {t([i18nRoot, "posted"])}
          </Link>
          <Typography.LabelMd as="span">
            {t([i18nRoot, "in"])}
          </Typography.LabelMd>
          <Link to={spaceUrl} {...navLinkProps}>
            {spaceName}
          </Link>
        </>
      );

    case COMMENT_TYPE:
      return (
        <>
          <Typography.LabelMd as="span">
            {t([i18nRoot, "commented"])}
          </Typography.LabelMd>
          <Typography.LabelMd as="span">
            {t([i18nRoot, "in"])}
          </Typography.LabelMd>
          <Link
            to={postUrl}
            hash={`comment_wrapper_${flaggedContentableId}`}
            {...navLinkProps}
          >
            {postTitle}
          </Link>
        </>
      );

    case CONNECT_CONNECTION_TYPE:
      if (!recipientName) {
        return null;
      }
      return (
        <>
          <Typography.LabelMd as="span" color="text-dark">
            {t([i18nRoot, "sent_to"])}
          </Typography.LabelMd>
          {recipientUrl ? (
            <Link to={recipientUrl} {...navLinkProps}>
              {recipientName}
            </Link>
          ) : (
            <Typography.LabelMd as="span" color="text-dark" weight="semibold">
              {recipientName}
            </Typography.LabelMd>
          )}
        </>
      );

    case CHAT_ROOM_MESSAGE_TYPE:
      return (
        <>
          <Typography.LabelMd as="span">
            {t([i18nRoot, "chat_message_action"])}
          </Typography.LabelMd>
          {spaceName && (
            <>
              <Typography.LabelMd as="span">
                {t([i18nRoot, "in"])}
              </Typography.LabelMd>
              <Link to={chatMessageUrl ?? spaceUrl} {...navLinkProps}>
                {spaceName}
              </Link>
            </>
          )}
        </>
      );

    default:
      return null;
  }
};
